import { useState } from 'react'
import api from '@/services/api'
import { toast } from 'sonner'
import type { Contact } from '@/types'

export function useEmpresaContactsActions(empresaId: string, onRefresh: () => void) {
  const [editingContact, setEditingContact] = useState<string | null>(null)
  const [contactEditData, setContactEditData] = useState<Partial<Contact>>({})
  const [saving, setSaving] = useState(false)
  const [syncing, setSyncing] = useState(false)

  const handleSaveContact = async () => {
    if (!contactEditData.nome) {
      toast.error('Nome é obrigatório')
      return
    }
    setSaving(true)
    try {
      if (editingContact === 'new') {
        await api.post('/contatos', {
          ...contactEditData,
          empresaId,
        })
        toast.success('Contato adicionado com sucesso')
      } else {
        await api.patch(`/contatos/${editingContact}`, contactEditData)
        toast.success('Contato atualizado com sucesso')
      }
      setEditingContact(null)
      onRefresh()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      toast.error(axiosErr.response?.data?.message || 'Erro ao salvar contato')
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteContact = async (contatoId: string) => {
    if (!window.confirm('Excluir este contato?')) return
    try {
      await api.delete(`/contatos/${contatoId}`)
      toast.success('Contato excluído')
      onRefresh()
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { message?: string } } }
      toast.error(axiosErr.response?.data?.message || 'Erro ao excluir contato')
    }
  }

  const handleSyncAvatar = async (contatoId: string) => {
    setSyncing(true)
    try {
      const resp = await api.post(`/contatos/${contatoId}/sync-avatar`)
      toast.success('Avatar sincronizado')
      const fotoUrl = resp.data.contato?.fotoUrl ?? resp.data.data?.fotoUrl
      if (fotoUrl) {
        setContactEditData((prev) => ({ ...prev, fotoUrl }))
      }
      onRefresh()
    } catch {
      toast.error('Erro ao sincronizar avatar')
    } finally {
      setSyncing(false)
    }
  }

  const startNewContact = () => {
    setEditingContact('new')
    setContactEditData({
      nome: '',
      cargo: '',
      email: '',
      telefone: '',
      decisor: false,
      linkedin: '',
    })
  }

  const startEditContact = (contato: Contact) => {
    setEditingContact(contato.id)
    setContactEditData({ ...contato })
  }

  return {
    editingContact,
    setEditingContact,
    contactEditData,
    setContactEditData,
    saving,
    syncing,
    handleSaveContact,
    handleDeleteContact,
    handleSyncAvatar,
    startNewContact,
    startEditContact,
  }
}
